import { ArrowDown, Globe, UserCheck, Filter, UserPlus, PhoneCall, Briefcase } from "lucide-react";
import { Container, Reveal, SectionHeader } from "./common";

const workflow = [
    [Globe, "Website"],
    [UserPlus, "Lead Capture"],
    [Filter, "Qualification"],
    [UserCheck, "Assignment"],
    [PhoneCall, "Follow-up"],
    [Briefcase, "Opportunity"],
];

const leads = [
    { name: "Ananya Iyer", source: "Website", owner: "Rahul S.", status: "Qualified", score: 92, next: "Site visit · Today 3 PM", last: "2h ago" },
    { name: "Vikram Menon", source: "Google Ads", owner: "Priya N.", status: "Contacted", score: 74, next: "Demo call · Tomorrow", last: "5h ago" },
    { name: "Sara D'Souza", source: "Referral", owner: "Arun T.", status: "New", score: 61, next: "Intro WhatsApp · 4 PM", last: "1d ago" },
    { name: "Rohan Kulkarni", source: "IndiaMart", owner: "Rahul S.", status: "Qualified", score: 88, next: "Proposal review · Fri", last: "3h ago" },
];

const timeline = [
    { time: "09:12", text: "Lead captured from website form", dot: "bg-[#16A34A]" },
    { time: "09:12", text: "Auto-assigned to Rahul Sharma (South region)", dot: "bg-[#16A34A]" },
    { time: "09:40", text: "Welcome email + WhatsApp sent", dot: "bg-[#86EFAC]" },
    { time: "11:05", text: "First call completed · marked Interested", dot: "bg-[#86EFAC]" },
    { time: "14:30", text: "Site visit scheduled for today 3 PM", dot: "bg-[#E5E7EB]" },
];

const statusStyle = {
    Qualified: "bg-[#F0FDF4] text-[#15803D] border-[#BBF7D0]",
    Contacted: "bg-blue-50 text-blue-600 border-blue-100",
    New: "bg-slate-50 text-slate-600 border-slate-200",
};

const LeadManagement = () => (
    <section id="lead-management" data-testid="lead-management-section" className="bg-[#F7FEF9]/60 py-20 lg:py-28">
        <Container>
            <div className="grid items-center gap-12 lg:grid-cols-[1fr_1.15fr] lg:gap-16">
                <div className="min-w-0">
                    <SectionHeader
                        align="left"
                        eyebrow="Lead Management"
                        title="Never lose a lead again."
                        sub="Bring leads from your website, campaigns, forms and sales teams into one organized workspace — scored, assigned and followed up, automatically."
                    />
                    {/* Workflow */}
                    <Reveal delay={0.2}>
                        <div data-testid="lead-management-workflow-mockup" className="mt-9 flex flex-col gap-0">
                            {workflow.map(([Icon, label], i) => (
                                <div key={label} className="flex flex-col items-start">
                                    <div className="flex items-center gap-3">
                                        <span className={`flex h-9 w-9 items-center justify-center rounded-xl border ${i === workflow.length - 1 ? "border-[#16A34A] bg-[#16A34A]" : "border-[#E5E7EB] bg-white"} shadow-sm`}>
                                            <Icon className={`h-4 w-4 ${i === workflow.length - 1 ? "text-white" : "text-[#16A34A]"}`} />
                                        </span>
                                        <span className="text-sm font-semibold text-[#111827]">{label}</span>
                                        {i === 0 && <span className="rounded-full bg-[#F0FDF4] px-2 py-0.5 text-[10px] font-bold text-[#15803D]">Source</span>}
                                        {i === workflow.length - 1 && <span className="rounded-full bg-[#F0FDF4] px-2 py-0.5 text-[10px] font-bold text-[#15803D]">Revenue</span>}
                                    </div>
                                    {i < workflow.length - 1 && (
                                        <ArrowDown className="ml-[15px] my-0.5 h-3.5 w-3.5 text-[#16A34A]/50" />
                                    )}
                                </div>
                            ))}
                        </div>
                    </Reveal>
                </div>

                {/* Lead workspace mockup */}
                <Reveal delay={0.15} className="min-w-0">
                    <div className="overflow-hidden rounded-2xl border border-[#E5E7EB] bg-white shadow-xl shadow-slate-900/[0.06]">
                        <div className="flex items-center justify-between border-b border-[#F1F5F9] px-5 py-3.5">
                            <p className="font-display text-sm font-bold text-[#111827]">Leads Workspace</p>
                            <span className="rounded-full bg-[#F0FDF4] px-2.5 py-1 text-[10px] font-bold text-[#15803D]">430 new this month</span>
                        </div>
                        <div className="overflow-x-auto">
                            <table className="w-full min-w-[560px] text-left">
                                <thead>
                                    <tr className="border-b border-[#F1F5F9] text-[10px] font-bold uppercase tracking-wider text-[#94A3B8]">
                                        <th className="px-5 py-2.5">Lead</th>
                                        <th className="px-3 py-2.5">Source</th>
                                        <th className="px-3 py-2.5">Owner</th>
                                        <th className="px-3 py-2.5">Status</th>
                                        <th className="px-3 py-2.5">Score</th>
                                        <th className="px-5 py-2.5">Next activity</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {leads.map((l) => (
                                        <tr key={l.name} className="border-b border-[#F8FAFC] last:border-0 hover:bg-[#F7FEF9]">
                                            <td className="px-5 py-3">
                                                <p className="text-[12px] font-semibold text-[#111827]">{l.name}</p>
                                                <p className="text-[10px] text-[#94A3B8]">Last touch {l.last}</p>
                                            </td>
                                            <td className="px-3 py-3 text-[11px] font-medium text-[#64748B]">{l.source}</td>
                                            <td className="px-3 py-3 text-[11px] font-medium text-[#64748B]">{l.owner}</td>
                                            <td className="px-3 py-3">
                                                <span className={`rounded-full border px-2 py-0.5 text-[9px] font-bold ${statusStyle[l.status]}`}>{l.status}</span>
                                            </td>
                                            <td className="px-3 py-3">
                                                <div className="flex items-center gap-1.5">
                                                    <div className="h-1.5 w-10 rounded-full bg-[#F1F5F9]">
                                                        <div className="h-1.5 rounded-full bg-[#16A34A]" style={{ width: `${l.score}%` }} />
                                                    </div>
                                                    <span className="text-[10px] font-bold text-[#111827]">{l.score}</span>
                                                </div>
                                            </td>
                                            <td className="px-5 py-3 text-[11px] font-medium text-[#334155]">{l.next}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                        {/* Timeline */}
                        <div className="border-t border-[#F1F5F9] bg-[#FBFDFC] px-5 py-4">
                            <p className="mb-3 text-[10px] font-bold uppercase tracking-wider text-[#94A3B8]">Lead timeline · Ananya Iyer</p>
                            <div className="grid gap-2">
                                {timeline.map((t, i) => (
                                    <div key={i} className="flex items-center gap-3">
                                        <span className="w-9 shrink-0 text-[10px] font-semibold text-[#94A3B8]">{t.time}</span>
                                        <span className={`h-1.5 w-1.5 shrink-0 rounded-full ${t.dot}`} />
                                        <span className="text-[11px] font-medium text-[#475569]">{t.text}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </Reveal>
            </div>
        </Container>
    </section>
);

export default LeadManagement;
